/*
 * <BeButton> -- React wrapper for the .be-button CSS component: the BeOS R5
 * push button (classic grey 3D bevel). Reconstructed from the byte-verified R5
 * libbe.so decompile (rdef_src/libbe_r5/reconstructed/BButton.Draw.match.cpp);
 * see components/button.css for the bevel/color provenance.
 *
 * Renders a real <button> (semantic + keyboard-operable). Pressed/focus/disabled
 * are the native :active / :focus-visible / :disabled states.
 *
 *   <BeButton onClick={...}>Cancel</BeButton>
 *   <BeButton variant="default" onClick={...}>OK</BeButton>
 *   <BeButton disabled>Unavailable</BeButton>
 */

import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeButtonVariant = "normal" | "default" | "danger";

export type BeButtonProps = BeProps<
	"button",
	{
		/** "default" draws the R5 grey default-button ring (the focused-action button);
		 * "danger" inks the label for a destructive action. */
		variant?: BeButtonVariant;
		/** Square, label-width dropped -- for a glyph rather than text. Sized from
		 * --be-button-h; set that on the element to resize one. */
		icon?: boolean;
	}
>;

export const BeButton = beComponent<BeButtonProps, HTMLButtonElement>("BeButton", (props, be) => {
	const { variant = "normal", icon, scale, className, style, type, children, ...rest } = props;

	return (
		<button
			type={type ?? "button"}
			{...rest}
			{...be("be-button", variant === "default" && "is-default", variant === "danger" && "is-danger", icon && "is-icon")}
		>
			{/* The label is wrapped in its own RUN element on purpose. The pixel-font
			 * treatments (the Gecko fringe blend, the device-grid snap) can only be
			 * applied to a transparent text run: mix-blend-mode on the <button>
			 * itself would multiply the button's BACKGROUND against the page and
			 * darken the control. See @beos-css/fonts-swis721 pixel-perfect.css. */}
			{pixelText(children)}
		</button>
	);
});
